import { UnitOfWorkItem } from "../../utilities/unit-of-work-observer";
import { QueryFilter } from "../queries/queries.models";
import { OdataGridListView } from "../../views/components/odata-grid-list-view/odata-grid-list-view";
import { PersistUserRequest } from "../manage/user.models";
import { LookupValue } from "../lookups/lookup.models";
import * as moment from "moment";
import { computedFrom } from "aurelia-binding";
import { BaseValidationResponse } from "../validations/validations.models";
import { IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";

export interface CustomerModel extends IEntityTypeIdentifier {
    customerId: number;
    name: string;
    branchId: number;
    isHosted: boolean;
    isRuleAutomationEnabled: boolean;
    isActive: boolean;
    businessUnits: BusinessUnitModel[];
    customerVettingLists: CustomerVettingListModel[];
    customerSirePurchaserNames: CustomerSirePurchaserNameModel[];
    customerDomains: CustomerDomainModel[];
    customerType?: string;
    code: string;
    isDataSubmission: boolean;
    defaultVoyageIncludesBallastLeg: boolean;
    shouldAddVesselsToVettingList: boolean;
    useMfa: boolean;
    isMepTrial: boolean;
    mepTrialExpiryDate?: Date;
    isVettingCustomer: boolean;
    shouldPassToRightShipVettingQueue: boolean;
    canInvoice: boolean;
    windwardClientId: string;
    windwardClientSecret: string;
    windwardVoiClientId: string;
    windwardVoiClientSecret: string;
    windwardVoiId: string;
    windwardInProgressVoiId: string;
    refinitiveOverrideDefault?: boolean;
    refinitiveGroupId?: string;
    customerFormTypes?: CustomerFormType[];
    customerNote?: string;
    purpleTracConfig?: CustomerPurpleTracConfig;
}

export interface CustomerPurpleTracConfig {
    customerPurpleTracConfigId?: number;
    customerId?: number;
    apiUser: string;
    apiKey: string;
    carbonRating: boolean;
}

export interface CustomerFormType {
    customerId: number;
    formTypeId: number;
}

export interface CustomerSirePurchaserNameModel extends IEntityTypeIdentifier{
    customerSirePurchaserNameId?: number;
    customerId?: number;
    name: string;
}

export interface CustomerDomainModel extends IEntityTypeIdentifier{
    customerDomainId?: number;
    customerId?: number;
    name: string;
}

export interface CustomerVettingListModel extends IEntityTypeIdentifier{
    listId: number;
}

export interface BaseRuleConfigParameterModel {
    ruleConfigParameterId: number;
    vettingTypeId: number;
    defaultDisplayText?: string;
    isCritical: boolean;
    useDefault?: boolean;
    copyVettingRuleParameter: boolean;
    ruleConfigParameter?: LookupValue;
    isAdvanced: boolean;
    convertedValue?: any;
    value: any;
    ruleConfigAdvancedParameters: RuleConfigAdvancedParameterModel[];
}

export interface CustomerRuleConfigParameterModel extends BaseRuleConfigParameterModel {
    customerRuleConfigParameterId?: number;
    customerId?: number;
    useDefault?: boolean;
}

export interface PersistRightShipQiUserRequest extends PersistUserRequest {
    accessableBusinessUnitIds?: number[];
    businessUnitId?: number;
    customerId?: number;
    resendInvite?: boolean;
    canAmmendVetRules?: boolean;
    userCompanyAssociations: UserCompanyAssociation[];
    isUserMfaExcluded: boolean;
}

export interface PersistRightshipRuleConfigParametersRequest {
    rightShipRuleConfigParameterId?: number;
    businessUnitRuleConfigParameterId?: number;
    customerRuleConfigParameterId?: number;
    changes: { [index: string]: any };
}

export interface RightshipRuleConfigParameterModel extends BaseRuleConfigParameterModel {
    rightshipRuleConfigParameterId?: number;
}

export interface BusinessUnitRuleConfigParameterModel extends BaseRuleConfigParameterModel {
    businessUnitRuleConfigParameterId?: number;
    businessUnitId?: number;
    useDefault?: boolean;
}

export interface RuleConfigAdvancedParameterModel {
    ruleConfigAdvancedParameterId?: number;
    customerRuleConfigParameterId?: number;
    businessUnitRuleConfigParameterId?: number;
    whenClauses: string;
    value: any;
    showDisplayText: boolean;
    displayText: string;
    whenClausesSql?: string;
    whenClausesJson?: QueryFilter[];
}

export interface GetBusinessUnitsResponse {
    businessUnits: BusinessUnitModel[];
    parameterIdsUsed: number[];
}

export interface GetCustomerResponse {
    customer: CustomerModel;
}

export interface GetCustomersResponse {
    customers: CustomerModel[];
    parameterIdsUsed: number[];
}

export interface CloneCustomerRoleRequest {
    roleId: number;
    roleName: string;
    roleDescription: string;
    branchId: number;
}

export interface GetCustomerBusinessUnitIdsResponse {
    businessUnitIds: number[];
}

export class CustomerDetailsModel {
    customerId: number;
    isMepTrial: boolean;
    mepTrialExpiryDate?: Date;

    constructor(customerId: number, isMepTrial: boolean, mepTrialExpiryDate?: Date) {
        this.customerId = customerId;
        this.isMepTrial = isMepTrial;
        this.mepTrialExpiryDate = mepTrialExpiryDate;
    }

    @computedFrom("mepTrialExpiryDate")
    get daysToExpire(): number {
        const daysDiff = moment.utc(this.mepTrialExpiryDate).diff(moment.utc().startOf('d'), 'd');

        return daysDiff;
    }

    public static getCustomerDetailModelFromRSUser(user): CustomerDetailsModel {
        const customerDetails = user && user.businessUnit ?
            new CustomerDetailsModel(user.businessUnit.customerId, user.businessUnit.isMepTrialCustomer, user.businessUnit.customermepTrialExpiryDate)
            : null;

        return customerDetails;
    }
}

export interface GetCustomersRuleParametersResponse {
    parameters: CustomerRuleConfigParameterModel[];
}

export interface GetBusinessUnitRuleParametersResponse {
    parameters: BusinessUnitRuleConfigParameterModel[];
    parameterIdsUsed: number[];
}

export interface GetBusinessUnitIdsResponse {
    businessUnitIds: number[];
}

export interface BusinessUnitVettingList {
    listId: number;
}

export interface BusinessUnitModel extends IEntityTypeIdentifier {
    parameterIdsUsed?: number[];
    businessUnitUsers: BusinessUnitUserModel[];
    businessUnitVetAlerts: BusinessUnitVetAlertModel[];
    businessUnitVettingLists: BusinessUnitVettingList[];
    businessUnitId: number;
    numberOfDaysBeforeVetExpires: number;
    numberOfDaysBeforeFormExpires: number;
    name: string;
    shouldAddVesselsToVettingList: boolean;
    customerId: number;
    isActive: boolean;
    showVetBanner: boolean;
    contactShipManagersDirectly: boolean;
    code: string;
    portsAndTerminalsDepartureDaysFromArrival: number;
    allowedDomains?: BusinessUnitDomainModel[];
}

export interface BusinessUnitDomainModel extends IEntityTypeIdentifier{
    businessUnitDomainId?: number;
    businessUnitId?: number;
    name: string;
}

export interface BusinessUnitVetAlertModel {
    id?: number;
    businessUnitVetAlertId?: number;
    businessUnitId?: number;
    customerId: number;
    vettingActionId: number;
    timeBeforeExpireInDays?: number;
    isActive: boolean;
    businessUnitVetAlertOnboardingConfigs: businessUnitVetAlertOnboardingConfig[]
    businessUnitVetAlertVesselTypes: BusinessUnitVetAlertVesselTypeModel[];
    businessUnitVetAlertIncoTerms: BusinessUnitVetAlertIncoTermModel[];
    businessUnitVetAlertPorts: businessUnitVetAlertPort[];
    businessUnitVetAlertRecipients: BusinessUnitVetAlertRecipientModel[];
    businessUnitVetAlertRfiTemplates: BusinessUnitVetAlertRfiTemplateModel[];
}

export interface BusinessUnitVetAlertRfiTemplateModel {
    rfiTemplateId: number;
}

export interface BusinessUnitVetAlertRecipientModel {
    businessUnitVetAlertRecipientId?: number;
    businessUnitVetAlertId?: number;
    emailRecipientTypeId: number;
    forVetRequester: boolean;
    forShipManager: boolean;
    forVoyageDataSubmitter: boolean;
    forFbrSubmitter: boolean;
    forCollaborators: boolean;
    email: string;
}

export interface BusinessUnitVetAlertIncoTermModel {
    businessUnitVetAlertIncoTermId?: number;
    businessUnitVetAlertId?: number;
    incoTermId: number;
}

export interface businessUnitVetAlertOnboardingConfig {
    onboardingConfigId: number;
}

export interface businessUnitVetAlertPort {
    portId: number;
}

export interface BusinessUnitVetAlertVesselTypeModel {
    businessUnitVetAlertVesselTypeId?: number;
    businessUnitVetAlertId?: number;
    vesselTypeId: number;
}

export interface BusinessUnitUserModel {
    businessUnitUserId: number;
    businessUnitId: number;
    userId: number;
    canAmmendVetRules: boolean;
}

export interface BusinessUnitFilteringModel {
    name: string,
    businessUnitId: number,
    customerId: number;
}

export interface SaveCustomerRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export interface SaveCustomerResponse extends BaseValidationResponse
{
    businessUnitIds: number[];
}

export enum CustomerSubscriptionType {
    LimitedSubscription = 1,
    FullSubscription = 2
}

export interface SaveBusinessUnitRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
}

export enum CustomerStepEnum {
    CustomerDetails = 1,
    CustomerRuleParameters = 2,
    CustomerRuleHistoryTrail = 3,
    BusinessUnits = 4,
    ManageRoles = 5,
    ManagePeople = 6,
    ManagePendingPeople = 7,
    ManageRules = 8,
    CustomerReportsSettings = 9,
    SharedActivities = 10,
    RiskCategorySettings = 11,
    CustomerSubscriptions = 12,
    CustomerSanctions = 13,
    CustomerFormsSetting = 14,
    CustomerDueDiligenceSettings = 15,
}

export interface ManageBusinessUnitPane {
    uri?: string;
    title: string;
    selectedItem?: CustomerModel;
    id: string;
    parentId: string;
    initialFilters?: QueryFilter[];
    searchFilters?: string[];
    showCloseButton: boolean;
    listVM?: OdataGridListView;
    element?: HTMLElement;
    fallbackText?: string;
}

export interface UserCompanyAssociation {
    userCompanyAssociationId: number
    userId: number;
    companyId: number;
    companyName: string;
    otherCompany: string;
}

export interface MoveUserToCustomerRequest {
    userIds: number[];
    customerId: number;
    businessUnitId: number;
    roleId: number;
    isMoveToRSUser: boolean;
}

export interface MoveUserToCustomerResponse {
    isSuccess: boolean;
    message: string;
}

export interface GetCustomerAllowedDomainsResponse {
    allowedDomains: string[];
}

export interface UpdateCustomerAllowedDomainsResponse {
    allowedDomains: string[];
}

export interface UpdateCustomerAllowedDomainsRequest {
    customerId: number;
    allowedDomains: string[];
}

export interface UpdateBusinessUnitAllowedDomainsRequest {
    businessUnitId: number;
    allowedDomains: string[];
}

export interface UpdateBusinessUnitAllowedDomainsResponse {
    allowedDomains: string[];
}

export interface BulkChangeUserRoleRequest{
    userIds: number[];
    newRoleId: number;    
}
